import React from "react";

export type SageImportReadPosition = {
  name: string;
  quantity: number | null;
  unit: string;
  unitPrice: number | null;
};

export type SageImportReadDocument = {
  id: string;
  invoiceNumber: string;
  issueDate: string | null;
  partyName: string | null;
  documentType: string;
  net: number | string | null;
  tax: number | string | null;
  gross: number | string | null;
  status: string;
  positionSumStatus: string | null;
  positions: SageImportReadPosition[];
};

function formatIsoDate(value: string | null): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  if (!match) return "—";
  return `${match[3]}.${match[2]}.${match[1]}`;
}

function formatMoney(value: number | string | null): string {
  if (value == null || value === "") return "—";
  const amount = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(amount)) return "—";
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(amount);
}

function paymentStatusLabel(status: string, documentType: string): string {
  if (documentType === "invoice_correction" && status === "paid") return "Ausbezahlt";
  if (status === "paid") return "Bezahlt";
  if (status === "overdue") return "Überfällig";
  if (status === "open") return "Offen";
  if (status === "cancelled") return "Storniert";
  return status || "—";
}

export function SageImportInvoiceReadView({ document }: { document: SageImportReadDocument }) {
  return (
    <article className="rounded-md border border-app-border bg-app-surface px-4 py-4 text-sm text-app-text">
      <h2 className="text-base font-semibold">{document.invoiceNumber || "Beleg"}</h2>
      <p className="mt-3">Aus Sage übernommen, nicht änderbar.</p>
      {document.positionSumStatus === "mismatch" ? (
        <p className="mt-1">Positionssumme weicht vom Rechnungsbetrag ab, maßgeblich ist der Rechnungsbetrag.</p>
      ) : null}
      <dl className="mt-4 grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-1">
        <dt className="text-app-text-subtle">Belegdatum</dt>
        <dd>{formatIsoDate(document.issueDate)}</dd>
        <dt className="text-app-text-subtle">Kunde / Empfänger</dt>
        <dd>{document.partyName?.trim() || "—"}</dd>
        <dt className="text-app-text-subtle">Netto</dt>
        <dd className="tabular-nums">{formatMoney(document.net)}</dd>
        <dt className="text-app-text-subtle">Steuer</dt>
        <dd className="tabular-nums">{formatMoney(document.tax)}</dd>
        <dt className="text-app-text-subtle">Brutto</dt>
        <dd className="tabular-nums">{formatMoney(document.gross)}</dd>
        <dt className="text-app-text-subtle">Zahlstatus</dt>
        <dd>{paymentStatusLabel(document.status, document.documentType)}</dd>
      </dl>
      <h3 className="mt-4 font-semibold">Positionen</h3>
      {document.positions.length === 0 ? (
        <p className="mt-1 text-app-text-subtle">Keine Positionen gespeichert.</p>
      ) : (
        <table className="mt-2 w-full text-left">
          <thead>
            <tr className="text-app-text-subtle">
              <th className="py-1 pr-2 font-medium">Beschreibung</th>
              <th className="py-1 pr-2 font-medium">Menge</th>
              <th className="py-1 pr-2 font-medium">Einheit</th>
              <th className="py-1 font-medium">Preis</th>
            </tr>
          </thead>
          <tbody>
            {document.positions.map((line, index) => (
              <tr key={`${line.name}-${index}`} className="border-t border-app-border">
                <td className="py-1 pr-2">{line.name || "—"}</td>
                <td className="py-1 pr-2 tabular-nums">{line.quantity ?? "—"}</td>
                <td className="py-1 pr-2">{line.unit || "—"}</td>
                <td className="py-1 tabular-nums">{formatMoney(line.unitPrice)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </article>
  );
}
